import React, { useEffect, useRef, useState } from 'react';
import LinkButton from '../atoms/LinkButton';
import LazyImage from '../atoms/LazyImage';
import { activeOffer } from '../../data/activeOffer';
import { HERO_VIDEO, getResponsiveImage } from '../../data/mediaManifest';

const HERO_POSTER_ORIGINAL = '/improve_passing.JPG';
const HERO_CLASSROOM_ORIGINAL = '/class_looks_at_heatmap_on_big_screen.png';

const heroPoster = getResponsiveImage(HERO_POSTER_ORIGINAL);
const heroClassroom = getResponsiveImage(HERO_CLASSROOM_ORIGINAL);

const readMotionPreference = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return { known: false, reduce: false };
  }

  return {
    known: true,
    reduce: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  };
};

const HeroSection = () => {
  const videoRef = useRef(null);
  const initialMotion = readMotionPreference();
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(initialMotion.reduce);
  const [isPaused, setIsPaused] = useState(initialMotion.reduce);
  const [showVideo, setShowVideo] = useState(initialMotion.known && !initialMotion.reduce);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const applyPreference = (matches) => {
      setPrefersReducedMotion(matches);
      if (matches) {
        setShowVideo(false);
        setIsPaused(true);
      } else {
        setShowVideo(true);
        setIsPaused(false);
      }
    };

    applyPreference(mediaQuery.matches);

    const handleChange = (event) => applyPreference(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (!showVideo || prefersReducedMotion || !videoRef.current) {
      return undefined;
    }

    videoRef.current.play().catch(() => {
      setShowVideo(false);
    });

    return undefined;
  }, [showVideo, prefersReducedMotion]);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video || !showVideo) {
      return;
    }

    if (video.paused) {
      video.play().catch(() => setShowVideo(false));
      setIsPaused(false);
    } else {
      video.pause();
      setIsPaused(true);
    }
  };

  const showMotionVideo = showVideo && !prefersReducedMotion;

  return (
    <section className="relative min-h-[100dvh] overflow-hidden bg-navy">
      {showMotionVideo ? (
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={heroPoster.src}
          aria-hidden="true"
        >
          <source src={HERO_VIDEO.sd} type="video/mp4" media="(max-width: 768px)" />
          <source src={HERO_VIDEO.hd} type="video/mp4" />
        </video>
      ) : (
        <LazyImage
          src={heroPoster.src}
          srcSet={heroPoster.srcSet}
          sizes={heroPoster.sizes}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
          fetchPriority="high"
          width={1280}
          height={720}
        />
      )}

      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />

      <div className="relative z-10 dr-container flex min-h-[100dvh] flex-col justify-center py-24 md:py-28">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7 text-white">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight">
              We make learning powerful skills{' '}
              <span className="bg-yellow text-navy px-2 py-0.5 rounded-sm inline-block">
                feel like play.
              </span>
            </h1>
            <p className="text-lg md:text-xl font-normal mb-8 max-w-prose text-white/95">
              With footyLab, students turn their own soccer game into Python code,
              meaningful data, and something they&apos;re proud to create.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-4">
              <LinkButton
                to={activeOffer.destination}
                variant="primary"
                className="text-lg py-3 px-6"
              >
                {activeOffer.label}
              </LinkButton>
              <LinkButton
                to="/#how-it-works"
                className="text-lg py-3 px-6"
              >
                Explore how it works
              </LinkButton>
            </div>
          </div>

          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <LazyImage
              src={heroClassroom.src}
              srcSet={heroClassroom.srcSet}
              sizes={heroClassroom.sizes}
              alt="Students viewing a soccer data heatmap on a classroom screen"
              className="w-full max-w-md rounded-2xl border border-white/20 shadow-lg object-cover aspect-[4/3]"
              loading="eager"
              width={800}
              height={600}
            />
          </div>
        </div>
      </div>

      {showMotionVideo && (
        <button
          type="button"
          onClick={togglePlayback}
          aria-label={isPaused ? 'Play hero video' : 'Pause hero video'}
          aria-pressed={!isPaused}
          className="absolute bottom-6 right-6 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-offset-2 motion-reduce:transition-none transition-colors duration-150"
        >
          {isPaused ? (
            <span aria-hidden="true">▶</span>
          ) : (
            <span aria-hidden="true">❚❚</span>
          )}
        </button>
      )}
    </section>
  );
};

export default HeroSection;
